// DEV_PATH:
// http://localhost:5173/dashboard/energy-audit/announcements

import { type ReactNode, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useFetchNewEnergyAuditsForAuditor } from "@/features/energy-audit/hooks/use-energy-audits.ts"
import { Button, Input, Modal, Table } from "@/shared/components"
import { formatDateDDMMYY } from "@/shared/components/common/chart/utils.ts"
import PaginationNew from "@/shared/components/common/pagination/PaginationNew.tsx"
import { Download4 } from "@/shared/components/ui/icons"
import { tCommon, tEnergyAudit } from "@/shared/config/i18n/index.ts"
import { usePagination } from "@/shared/hooks"
import { StatusBadge } from "@/shared/ui/status-badges/index.tsx"
import { EnergyAuditWrapper } from "../../components/Wrapper"
import Header from "./header.tsx"
import { HEADERS } from "./tableConfig"

const STATUS_CONFIG = {
  new: { label: "status_energy_audit.new", type: "warning" },
  portfolio_shared: { label: "status_energy_audit.portfolio_shared", type: "success" },
  pending: { label: "status_energy_audit.contract_uploaded", type: "confirmed" },
  rejected: { label: "status_energy_audit.contract_rejected", type: "danger" },
  // accepted: { label: "status_energy_audit.contract_uploaded", type: "confirmed" },
  cancelled: { label: "status_energy_audit.contract_cancelled", type: "danger" }
} as const

export const EnergyAuditAnnouncementsList = () => {
  const { t } = useTranslation("common")
  // Helpers
  const pagination = usePagination()
  const pager = pagination.pager

  // States
  const [filters, setFilters] = useState<{ search?: string; status?: string }>({})
  const [showUpload, setShowUpload] = useState<number | null>(null)
  const [showDownload, setShowDownload] = useState<number | null>(null)

  // Queries
  const { data: response, isFetching } = useFetchNewEnergyAuditsForAuditor({
    ...filters,
    ...pager
  })
  const energyAudits = response?.results

  // Functions
  const getStatus = (status: keyof typeof STATUS_CONFIG) => {
    const config = STATUS_CONFIG[status]
    if (!config) {
      return <StatusBadge label={status} type="offline" />
    }
    return <StatusBadge label={tEnergyAudit(config.label as any)} type={config.type} />
  }

  const data: ReactNode[][] = useMemo(() => {
    return (
      energyAudits?.map((audit, index) => [
        index + 1,
        audit.company?.name,
        audit.energy_audit_type,
        formatDateDDMMYY(audit.created_at),
        getStatus(audit.last_application_status as keyof typeof STATUS_CONFIG),
        formatDateDDMMYY(audit.expire_date),
        audit.last_application_status !== "accepted" ? (
          <div key="c1" className="flex justify-center gap-2 ">
            <Button to={`detail/${audit.request_id}`} variant="icon" className="icon-action h-[35px] min-w-[186px] bg-white">
              {t("more-details")}
            </Button>
          </div>
        ) : null
      ]) || []
    )
  }, [energyAudits])

  return (
    <>
      {/*TODO: We should check this for child pages*/}
      {/*<Breadcrumb className="m-6 mb-0 size-auto" />*/}
      <EnergyAuditWrapper header={<Header filters={filters} onFilter={setFilters} />}>
        <div className="flex-1 overflow-auto">
          <Table className="pagination" loading={isFetching} headers={HEADERS} data={data} />
        </div>
        <PaginationNew response={response} {...pagination} className="rounded-b-xl" />
      </EnergyAuditWrapper>
      <Modal show={showDownload != null} title={tCommon("upload-file")} onClose={() => setShowDownload(null)} classNames={{ modal: "!w-[400px]" }}>
        <div className="flex flex-col gap-5">
          <div className="flex w-full flex-col gap-2">
            <div children={tEnergyAudit("technical_task")} />
            <div className="modal__download-item">
              <span className="flex-1">8934cn98.png</span>
              <Download4 size={18} />
            </div>
          </div>
          <div className="flex w-full flex-col gap-2">
            <div children={tEnergyAudit("enterprise_info")} />
            <div className="modal__download-item">
              <span className="flex-1">8934cn98.png</span>
              <Download4 size={18} />
            </div>
          </div>
        </div>
        <Button onClick={() => setShowDownload(null)} color="primary" variant="outlined" className="w-full">
          {tCommon("cancel")}
        </Button>
      </Modal>
      <Modal
        show={showUpload != null}
        title={tEnergyAudit("upload_contract")}
        onClose={() => setShowUpload(null)}
        classNames={{ modal: "!w-[400px]" }}
      >
        <form className="flex flex-col gap-5 pt-5" onSubmit={() => console.log("submitted")}>
          <Input label={tEnergyAudit("contract_number")} name="contract_number" placeholder={tEnergyAudit("contract_number")} />
          <Input label={tEnergyAudit("contract_deadline")} name="contract_deadline" placeholder={tEnergyAudit("contract_deadline")} />
        </form>
        <Button onClick={() => setShowUpload(null)} color="primary" variant="outlined" className="w-full">
          {tCommon("cancel")}
        </Button>
      </Modal>
    </>
  )
}
